import React, { useState, useEffect } from 'react';
import './UpdateNotification.css';

/**
 * UpdateNotification Component
 * Displays update notifications and progress for Electron app updates
 */
function UpdateNotification() {
  const [updateState, setUpdateState] = useState({
    available: false,
    downloading: false,
    downloaded: false,
    error: null,
    version: null,
    releaseNotes: null,
    progress: null
  });

  useEffect(() => {
    // Check if running in Electron
    if (!window.electron || !window.electron.updater) {
      return; // Not in Electron, no updates available
    }

    const { updater } = window.electron;

    // Set up event listeners
    const handleUpdateAvailable = (data) => {
      console.log('Update available:', data.version);
      setUpdateState(prev => ({
        ...prev,
        available: true,
        version: data.version,
        releaseNotes: data.releaseNotes,
        error: null
      }));
      // Note: autoUpdater.autoDownload is set to true in main.js,
      // so download will start automatically - no need to call downloadUpdate() manually
    };

    const handleUpdateNotAvailable = () => {
      // Silently handle - no update available
      setUpdateState(prev => ({
        ...prev,
        available: false
      }));
    };

    const handleDownloadProgress = (data) => {
      console.log('Download progress:', data.percent + '%');
      setUpdateState(prev => ({
        ...prev,
        downloading: true,
        progress: data.percent
      }));
    };

    const handleUpdateDownloaded = (data) => {
      console.log('Update downloaded:', data.version);
      setUpdateState(prev => ({
        ...prev,
        downloading: false,
        downloaded: true,
        version: data.version,
        releaseNotes: data.releaseNotes
      }));
      // Auto-install and restart after a short delay (silent install, no installer UI)
      setTimeout(() => {
        console.log('Installing update silently...');
        if (window.electron && window.electron.updater) {
          window.electron.updater.installUpdate();
        }
      }, 1000); // 1 second delay to show completion, then silent install
    };

    const handleUpdateError = (data) => {
      console.error('Update error:', data.message);
      setUpdateState(prev => ({
        ...prev,
        error: data.message,
        downloading: false
      }));
    };

    // Register listeners
    updater.onUpdateAvailable(handleUpdateAvailable);
    updater.onUpdateNotAvailable(handleUpdateNotAvailable);
    updater.onUpdateDownloadProgress(handleDownloadProgress);
    updater.onUpdateDownloaded(handleUpdateDownloaded);
    updater.onUpdateError(handleUpdateError);

    // Cleanup
    return () => {
      updater.removeAllListeners('update-available');
      updater.removeAllListeners('update-not-available');
      updater.removeAllListeners('update-download-progress');
      updater.removeAllListeners('update-downloaded');
      updater.removeAllListeners('update-error');
    };
  }, []);

  const handleDownload = () => {
    if (window.electron && window.electron.updater) {
      window.electron.updater.downloadUpdate();
    }
  };

  const handleInstall = () => {
    if (window.electron && window.electron.updater) {
      window.electron.updater.installUpdate();
    }
  };

  const handleDismiss = () => {
    setUpdateState(prev => ({
      ...prev,
      available: false,
      error: null
    }));
  };

  // Don't render if not in Electron or no updates
  if (!window.electron || !window.electron.updater) {
    return null;
  }

  // Show error notification
  if (updateState.error) {
    return (
      <div className="update-notification error">
        <div className="update-notification-content">
          <div className="update-notification-header">
            <span className="update-notification-title">Update Error</span>
            <button className="update-notification-close" onClick={handleDismiss}>×</button>
          </div>
          <div className="update-notification-body">
            <p>{updateState.error}</p>
          </div>
        </div>
      </div>
    );
  }

  // Show update available notification (download starts automatically via autoDownload)
  // This state shows briefly while download is starting
  if (updateState.available && !updateState.downloading && !updateState.downloaded) {
    return (
      <div className="update-lock-overlay">
        <div className="update-lock-content">
          <div className="update-lock-header">
            <h2 className="update-lock-title">Update Available</h2>
            <p className="update-lock-subtitle">Preparing to download update v{updateState.version}...</p>
          </div>
        </div>
      </div>
    );
  }

  // Show download progress with fullscreen lock overlay (obtrusive UI, but silent installer)
  if (updateState.downloading) {
    return (
      <>
        {/* Fullscreen lock overlay */}
        <div className="update-lock-overlay">
          <div className="update-lock-content">
            <div className="update-lock-header">
              <h2 className="update-lock-title">Updating Game</h2>
              <p className="update-lock-subtitle">Please wait while we download the latest version...</p>
            </div>
            <div className="update-lock-progress-container">
              <div className="update-lock-progress-bar">
                <div 
                  className="update-lock-progress-fill" 
                  style={{ width: `${updateState.progress || 0}%` }}
                />
              </div>
              <p className="update-lock-progress-text">
                {Math.round(updateState.progress || 0)}% complete
              </p>
              {updateState.progress !== null && updateState.progress > 0 && (
                <p className="update-lock-progress-details">
                  Downloading update v{updateState.version}...
                </p>
              )}
            </div>
          </div>
        </div>
      </>
    );
  }

  // Show update ready to install (will auto-restart after 1 second, silent install)
  if (updateState.downloaded) {
    return (
      <>
        {/* Fullscreen lock overlay for installation */}
        <div className="update-lock-overlay">
          <div className="update-lock-content">
            <div className="update-lock-header">
              <h2 className="update-lock-title">Update Ready</h2>
              <p className="update-lock-subtitle">Installing update v{updateState.version}...</p>
            </div>
            <div className="update-lock-progress-container">
              <div className="update-lock-progress-bar">
                <div className="update-lock-progress-fill" style={{ width: '100%' }} />
              </div>
              <p className="update-lock-progress-text">The game will restart automatically...</p>
              <p className="update-lock-progress-details">
                (Installer runs silently in the background)
              </p>
            </div>
          </div>
        </div>
      </>
    );
  }

  return null;
}

export default UpdateNotification;
